// Import request readers for quoted, bare, and `url(...)` declaration forms.
// Example: `@import url("a.css");` returns `a.css` without wrapper tokens.

// Build the raw import request token from one `@import` declaration.
export function readImportBody(statement: string): string | null {
  const importIndex = statement.toLowerCase().indexOf('@import')
  // Check whether the declaration contains an `@import` token.
  if (importIndex === -1) {
    // Return null for invalid lines without `@import`.
    return null
  }

  let index = importIndex + '@import'.length
  // Continue past spaces between the rule name and request token.
  while (index < statement.length && isWhitespace(statement[index])) {
    index += 1
  }

  const char = statement[index] ?? ''
  // Check whether the request uses a quoted token.
  if (char === "'" || char === '"') {
    // Return the parsed quoted request token.
    return readQuotedToken(statement, index)
  } else if (startsWithUrlCall(statement, index)) {
    // Return the parsed `url(...)` request token.
    return readUrlToken(statement, index + 4)
  } else {
    // Return the parsed bare request token.
    return readBareToken(statement, index)
  }
}

// Build one quoted request token without outer quotes.
function readQuotedToken(statement: string, start: number): string | null {
  const quote = statement[start] as "'" | '"'
  let index = start + 1
  // Start one quoted-token scan until the matching quote closes.
  while (index < statement.length) {
    const char = statement[index]
    const previous = index > start ? statement[index - 1] : ''
    if (char === quote && previous !== '\\') {
      // Return the unquoted request token when the quote closes.
      return statement.slice(start + 1, index)
    }
    index += 1
  }
  // Return null when the quoted request token never closes.
  return null
}

// Build one `url(...)` request token without the wrapper.
function readUrlToken(statement: string, start: number): string | null {
  const closeIndex = statement.indexOf(')', start)
  // Check whether the `url(...)` token closes correctly.
  if (closeIndex === -1) {
    // Return null for malformed `url(` tokens without `)`.
    return null
  }

  const inner = statement.slice(start, closeIndex).trim()
  // Check whether the inner `url(...)` payload keeps surrounding quotes.
  if ((inner.startsWith('"') && inner.endsWith('"')) || (inner.startsWith("'") && inner.endsWith("'"))) {
    // Return the inner request path without surrounding quotes.
    return inner.slice(1, -1)
  } else {
    // Return the raw inner request path for unquoted `url(...)`.
    return inner
  }
}

// Build one bare request token until whitespace or semicolon.
function readBareToken(statement: string, start: number): string | null {
  let index = start
  // Continue until the request token reaches whitespace or semicolon.
  while (index < statement.length && !isWhitespace(statement[index]) && statement[index] !== ';') {
    index += 1
  }

  const token = statement.slice(start, index).trim()
  // Check whether the parsed bare token is empty.
  if (!token) {
    // Return null when the bare import token is empty.
    return null
  } else {
    // Return the parsed bare request token.
    return token
  }
}

// Check whether the current slice starts with `url(`.
function startsWithUrlCall(statement: string, index: number): boolean {
  // Return true when the current slice matches a case-insensitive `url(` token.
  return statement.slice(index, index + 4).toLowerCase() === 'url('
}

// Check whether the current character is import-token whitespace.
function isWhitespace(char: string): boolean {
  // Return true for CSS whitespace characters accepted around import tokens.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}
